import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import baseContent from '../data/content.json';

type Content = typeof baseContent;

type ContentOverrides = {
  brand?: Partial<Content['brand']>;
  hero?: {
    headline?: string;
    subheadline?: string;
    ctaButtons?: Array<{ label?: string }>;
  };
  global?: {
    contactInfo?: Array<{ value?: string }>;
    socialLinks?: Array<{ link?: string }>;
  };
};

const ContentContext = createContext<Content>(baseContent);

export function useContent(): Content {
  return useContext(ContentContext);
}

function mergeContent(base: Content, overrides: ContentOverrides): Content {
  return {
    ...base,
    brand: { ...base.brand, ...overrides.brand },
    hero: {
      ...base.hero,
      ...(overrides.hero?.headline !== undefined ? { headline: overrides.hero.headline } : {}),
      ...(overrides.hero?.subheadline !== undefined ? { subheadline: overrides.hero.subheadline } : {}),
      ctaButtons: base.hero.ctaButtons.map((btn, i) => ({
        ...btn,
        ...(overrides.hero?.ctaButtons?.[i]?.label !== undefined
          ? { label: overrides.hero.ctaButtons[i].label as string }
          : {}),
      })),
    },
    global: {
      ...base.global,
      contactInfo: base.global.contactInfo.map((info, i) => ({
        ...info,
        ...(overrides.global?.contactInfo?.[i]?.value !== undefined
          ? { value: overrides.global.contactInfo[i].value as string }
          : {}),
      })),
      socialLinks: base.global.socialLinks.map((s, i) => ({
        ...s,
        ...(overrides.global?.socialLinks?.[i]?.link !== undefined
          ? { link: overrides.global.socialLinks[i].link as string }
          : {}),
      })),
    },
  };
}

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<Content>(baseContent);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/content.json')
      .then((res) => (res.ok ? res.json() : null))
      .then((overrides: ContentOverrides | null) => {
        if (!cancelled && overrides) {
          setContent((prev) => mergeContent(prev, overrides));
        }
      })
      .catch(() => {
        // Keep the static bundled content if the live-edit API is unreachable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}
